import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimeline, faChevronDown, faChevronRight, faClock, faRoute,
  faCookie, faKey, faCheckCircle, faExclamationTriangle, faCode,
  faTimes, faSync, faPlay
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';

const LoginWorkflowViewer = ({ profileId, isDarkMode, onClose, onTestReplay }) => {
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedExchanges, setExpandedExchanges] = useState(new Set([0])); // First exchange expanded by default
  const [activeTab, setActiveTab] = useState('timeline'); // 'timeline' or 'raw'
  const [exchangeTabs, setExchangeTabs] = useState({}); // Track request/response tabs per exchange

  useEffect(() => {
    loadWorkflow();
  }, [profileId]);

  const loadWorkflow = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/auth-profiles/${profileId}/workflow`);
      setWorkflow(response.data.workflow);
    } catch (error) {
      console.error('Failed to load workflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExchange = (index) => {
    const newExpanded = new Set(expandedExchanges);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedExchanges(newExpanded);
  };

  const getExchangeIcon = (type) => {
    switch (type) {
      case 'login_page_get': return '📄';
      case 'login_post': return '🔐';
      case 'redirect': return '↪️';
      case 'final_authenticated': return '✅';
      default: return '📡';
    }
  };

  const getExchangeLabel = (type) => {
    switch (type) {
      case 'login_page_get': return 'Login Page (GET)';
      case 'login_post': return 'Login POST';
      case 'redirect': return 'Redirect';
      case 'final_authenticated': return 'Final (Authenticated)';
      default: return 'Exchange';
    }
  };

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return isDarkMode ? 'text-green-400' : 'text-green-600';
    if (statusCode >= 300 && statusCode < 400) return isDarkMode ? 'text-yellow-400' : 'text-yellow-600';
    if (statusCode >= 400 && statusCode < 500) return isDarkMode ? 'text-orange-400' : 'text-orange-600';
    if (statusCode >= 500) return isDarkMode ? 'text-red-400' : 'text-red-600';
    return isDarkMode ? 'text-gray-400' : 'text-gray-600';
  };

  const formatHeaders = (headers) => {
    if (!headers) return {};
    // Convert headers object to key-value pairs
    if (typeof headers === 'object' && !Array.isArray(headers)) {
      return headers;
    }
    // If it's an array, convert to object
    if (Array.isArray(headers)) {
      return headers.reduce((acc, h) => ({ ...acc, [h.name]: h.value }), {});
    }
    return {};
  };

  const formatBody = (body, mode = 'pretty') => {
    if (!body) return mode === 'pretty' ? null : '';

    if (mode === 'raw') return typeof body === 'string' ? body : JSON.stringify(body);

    try {
      const parsed = typeof body === 'string' ? JSON.parse(body) : body;
      return parsed;
    } catch {
      return body;
    }
  };

  const getExchangeTab = (exchangeIndex, side) => {
    const key = `${exchangeIndex}-${side}`;
    return exchangeTabs[key] || 'pretty';
  };

  const setExchangeTab = (exchangeIndex, side, tab) => {
    setExchangeTabs(prev => ({
      ...prev,
      [`${exchangeIndex}-${side}`]: tab
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const containerClass = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
  const borderClass = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const hoverClass = isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  if (loading) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${isDarkMode ? 'bg-black bg-opacity-75' : 'bg-gray-900 bg-opacity-50'}`}>
        <div className={`${containerClass} p-6 rounded-lg shadow-xl`}>
          <FontAwesomeIcon icon={faSync} spin className="mr-2" />
          Loading workflow...
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${isDarkMode ? 'bg-black bg-opacity-75' : 'bg-gray-900 bg-opacity-50'}`}>
        <div className={`${containerClass} p-6 rounded-lg shadow-xl max-w-md`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">No Workflow</h3>
            <button onClick={onClose} className={`p-2 rounded ${hoverClass}`}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            No workflow was captured for this profile. Workflows are automatically captured when you login through the proxy.
          </p>
          <button
            onClick={onClose}
            className={`mt-4 w-full px-4 py-2 rounded ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDarkMode ? 'bg-black bg-opacity-75' : 'bg-gray-900 bg-opacity-50'}`}>
      <div className={`${containerClass} rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderClass}`}>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faTimeline} className="text-blue-500" />
            <div>
              <h2 className="text-xl font-semibold">Login Workflow</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Captured: {new Date(workflow.capturedAt).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded ${hoverClass}`}
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${borderClass}`}>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-4 py-2 ${activeTab === 'timeline' ? (isDarkMode ? 'border-b-2 border-blue-500 text-blue-400' : 'border-b-2 border-blue-500 text-blue-600') : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            <FontAwesomeIcon icon={faTimeline} className="mr-2" />
            Timeline View
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 ${activeTab === 'summary' ? (isDarkMode ? 'border-b-2 border-blue-500 text-blue-400' : 'border-b-2 border-blue-500 text-blue-600') : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            Summary
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'summary' && (
            <div className="space-y-4">
              {/* Workflow Metadata */}
              <div className={`p-4 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faRoute} className="text-blue-500" />
                  Workflow Details
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Total Duration:</span>
                    <span className="ml-2 font-mono">{workflow.metadata?.totalDuration || 0} ms</span>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Redirects:</span>
                    <span className="ml-2 font-mono">{workflow.metadata?.redirectCount || 0}</span>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Total Exchanges:</span>
                    <span className="ml-2 font-mono">{workflow.exchanges?.length || 0}</span>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Detection Method:</span>
                    <span className="ml-2 font-mono">{workflow.metadata?.detectionMethod || 'unknown'}</span>
                  </div>
                </div>
              </div>

              {/* Credentials */}
              {(workflow.credentials?.autoDetected || workflow.credentials?.manualMapping) && (
                <div className={`p-4 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-green-900 bg-opacity-20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faKey} className="text-green-500" />
                    Captured Credentials
                  </h3>
                  {workflow.credentials.autoDetected && (
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Source:</span>
                        <span className="font-semibold">Auto-detected</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          workflow.credentials.autoDetected.confidence === 'high' ? 'bg-green-600 text-white' :
                          workflow.credentials.autoDetected.confidence === 'medium' ? 'bg-yellow-600 text-white' :
                          'bg-orange-600 text-white'
                        }`}>
                          {workflow.credentials.autoDetected.confidence} confidence
                        </span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Username field:</span>
                        <code className="ml-2 px-2 py-0.5 rounded bg-black bg-opacity-20 text-xs">
                          {workflow.credentials.autoDetected.usernameField}
                        </code>
                        <span className="ml-2 text-green-400">= {workflow.credentials.autoDetected.usernameValue}</span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Password field:</span>
                        <code className="ml-2 px-2 py-0.5 rounded bg-black bg-opacity-20 text-xs">
                          {workflow.credentials.autoDetected.passwordField}
                        </code>
                        <span className="ml-2 text-yellow-400">= ***</span>
                      </div>
                    </div>
                  )}
                  {workflow.credentials.manualMapping && (
                    <div className="space-y-2 text-sm mt-2 pt-2 border-t border-green-700">
                      <div className="flex items-center gap-2">
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Source:</span>
                        <span className="font-semibold">Manually mapped</span>
                        <span className="text-xs text-gray-500">
                          by user on {new Date(workflow.credentials.manualMapping.mappedAt).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Username field:</span>
                        <code className="ml-2 px-2 py-0.5 rounded bg-black bg-opacity-20 text-xs">
                          {workflow.credentials.manualMapping.usernameField}
                        </code>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Password field:</span>
                        <code className="ml-2 px-2 py-0.5 rounded bg-black bg-opacity-20 text-xs">
                          {workflow.credentials.manualMapping.passwordField}
                        </code>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Success Indicators */}
              {workflow.successIndicators && (
                <div className={`p-4 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500" />
                    Success Indicators
                  </h3>
                  <div className="space-y-2 text-sm">
                    {workflow.successIndicators.finalUrl && (
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Final URL:</span>
                        <code className="ml-2 text-xs">{workflow.successIndicators.finalUrl}</code>
                      </div>
                    )}
                    {workflow.successIndicators.requiredCookies?.length > 0 && (
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Required Cookies:</span>
                        <span className="ml-2">{workflow.successIndicators.requiredCookies.join(', ')}</span>
                      </div>
                    )}
                    {workflow.successIndicators.bodyContains?.length > 0 && (
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Body Contains:</span>
                        <span className="ml-2">{workflow.successIndicators.bodyContains.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Replay Stats */}
              {workflow.replayStats && workflow.replayStats.totalReplays > 0 && (
                <div className={`p-4 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faPlay} className="text-purple-500" />
                    Replay Statistics
                  </h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Total Replays</div>
                      <div className="text-2xl font-bold">{workflow.replayStats.totalReplays}</div>
                    </div>
                    <div>
                      <div className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Successful</div>
                      <div className="text-2xl font-bold text-green-500">{workflow.replayStats.successfulReplays}</div>
                    </div>
                    <div>
                      <div className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Failed</div>
                      <div className="text-2xl font-bold text-red-500">{workflow.replayStats.failedReplays}</div>
                    </div>
                  </div>
                  {workflow.replayStats.lastReplayAt && (
                    <div className="mt-3 text-sm">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Last replay:</span>
                      <span className="ml-2">{new Date(workflow.replayStats.lastReplayAt).toLocaleString()}</span>
                      <span className={`ml-2 ${workflow.replayStats.lastReplaySuccess ? 'text-green-400' : 'text-red-400'}`}>
                        {workflow.replayStats.lastReplaySuccess ? '✓ Success' : '✗ Failed'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-3">
              {workflow.exchanges?.map((exchange, index) => (
                <div
                  key={index}
                  className={`border rounded-lg ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-white'}`}
                >
                  {/* Exchange Header */}
                  <button
                    onClick={() => toggleExchange(index)}
                    className={`w-full flex items-center justify-between p-3 ${hoverClass} rounded-t-lg`}
                  >
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon
                        icon={expandedExchanges.has(index) ? faChevronDown : faChevronRight}
                        className="text-xs"
                      />
                      <span className="text-lg">{getExchangeIcon(exchange.type)}</span>
                      <div className="text-left">
                        <div className="font-semibold text-sm">{index}. {getExchangeLabel(exchange.type)}</div>
                        <div className="text-xs text-gray-500">
                          {exchange.request.method} {exchange.request.url}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-sm ${getStatusColor(exchange.response.statusCode)}`}>
                        {exchange.response.statusCode}
                      </span>
                      {exchange.response.redirectLocation && (
                        <span className="text-xs bg-yellow-600 text-white px-2 py-0.5 rounded">
                          → Redirect
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Exchange Details - Side by Side */}
                  {expandedExchanges.has(index) && (
                    <div className={`border-t ${borderClass}`}>
                      <div className="grid grid-cols-2 divide-x ${borderClass}">
                        {/* Request Panel */}
                        <div className="flex flex-col">
                          {/* Request Header */}
                          <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-750' : 'bg-gray-100'} flex items-center justify-between border-b ${borderClass}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-blue-500 font-semibold">↑</span>
                              <span className="text-sm font-semibold">Request</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => copyToClipboard(JSON.stringify(exchange.request, null, 2))}
                                className="text-xs px-2 py-1 rounded hover:bg-gray-600"
                                title="Copy"
                              >
                                <FontAwesomeIcon icon={faCode} />
                              </button>
                            </div>
                          </div>

                          {/* Request Tabs */}
                          <div className={`flex border-b ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                            {['Pretty', 'Raw', 'Headers'].map(tab => (
                              <button
                                key={tab}
                                onClick={() => setExchangeTab(index, 'request', tab.toLowerCase())}
                                className={`px-3 py-1.5 text-xs ${
                                  getExchangeTab(index, 'request') === tab.toLowerCase()
                                    ? isDarkMode
                                      ? 'text-green-400 border-b-2 border-green-400'
                                      : 'text-green-600 border-b-2 border-green-600'
                                    : isDarkMode
                                    ? 'text-gray-400 hover:text-gray-300'
                                    : 'text-gray-600 hover:text-gray-700'
                                }`}
                              >
                                {tab}
                              </button>
                            ))}
                          </div>

                          {/* Request Content */}
                          <div className="flex-1 overflow-auto p-3">
                            {getExchangeTab(index, 'request') === 'pretty' && (
                              <div className="space-y-3 text-xs">
                                <div>
                                  <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} font-semibold mb-1`}>Method & URL</div>
                                  <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] font-mono">
                                      {exchange.request.method}
                                    </span>
                                    <code className={`flex-1 px-2 py-1 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} text-[10px]`}>
                                      {exchange.request.url}
                                    </code>
                                  </div>
                                </div>
                                {exchange.request.body && (
                                  <div>
                                    <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} font-semibold mb-1`}>Body</div>
                                    <pre className={`p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} overflow-x-auto text-[10px] font-mono`}>
                                      {JSON.stringify(formatBody(exchange.request.body, 'pretty'), null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}
                            {getExchangeTab(index, 'request') === 'raw' && (
                              <pre className={`p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} overflow-x-auto text-[10px] font-mono whitespace-pre-wrap`}>
                                {`${exchange.request.method} ${exchange.request.url}\n\n`}
                                {Object.entries(formatHeaders(exchange.request.headers)).map(([k, v]) => `${k}: ${v}`).join('\n')}
                                {exchange.request.body && `\n\n${formatBody(exchange.request.body, 'raw')}`}
                              </pre>
                            )}
                            {getExchangeTab(index, 'request') === 'headers' && (
                              <div className="space-y-1">
                                {Object.entries(formatHeaders(exchange.request.headers)).map(([name, value]) => (
                                  <div key={name} className={`p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <div className="text-[10px] font-semibold text-blue-400">{name}</div>
                                    <div className="text-[10px] font-mono text-gray-300 break-all">{value}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Response Panel */}
                        <div className="flex flex-col">
                          {/* Response Header */}
                          <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-750' : 'bg-gray-100'} flex items-center justify-between border-b ${borderClass}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-green-500 font-semibold">↓</span>
                              <span className="text-sm font-semibold">Response</span>
                              <span className={`text-xs px-2 py-0.5 rounded font-mono ${getStatusColor(exchange.response.statusCode)}`}>
                                {exchange.response.statusCode}
                              </span>
                              {exchange.response.body && (
                                <span className="text-xs text-gray-500">
                                  {(exchange.response.body.length / 1024).toFixed(1)}KB
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => copyToClipboard(JSON.stringify(exchange.response, null, 2))}
                              className="text-xs px-2 py-1 rounded hover:bg-gray-600"
                              title="Copy"
                            >
                              <FontAwesomeIcon icon={faCode} />
                            </button>
                          </div>

                          {/* Response Tabs */}
                          <div className={`flex border-b ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                            {['Pretty', 'Raw', 'Headers'].map(tab => (
                              <button
                                key={tab}
                                onClick={() => setExchangeTab(index, 'response', tab.toLowerCase())}
                                className={`px-3 py-1.5 text-xs ${
                                  getExchangeTab(index, 'response') === tab.toLowerCase()
                                    ? isDarkMode
                                      ? 'text-green-400 border-b-2 border-green-400'
                                      : 'text-green-600 border-b-2 border-green-600'
                                    : isDarkMode
                                    ? 'text-gray-400 hover:text-gray-300'
                                    : 'text-gray-600 hover:text-gray-700'
                                }`}
                              >
                                {tab}
                              </button>
                            ))}
                          </div>

                          {/* Response Content */}
                          <div className="flex-1 overflow-auto p-3">
                            {getExchangeTab(index, 'response') === 'pretty' && (
                              <div className="space-y-3 text-xs">
                                {exchange.response.cookies?.length > 0 && (
                                  <div>
                                    <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} font-semibold mb-1 flex items-center gap-1`}>
                                      <FontAwesomeIcon icon={faCookie} className="text-orange-500" />
                                      Set Cookies ({exchange.response.cookies.length})
                                    </div>
                                    <div className="space-y-1">
                                      {exchange.response.cookies.map((cookie, i) => (
                                        <div key={i} className={`p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                          <div className="text-[10px] font-semibold text-orange-400">{cookie.name}</div>
                                          <div className="text-[10px] font-mono text-gray-300 break-all">{cookie.value}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {exchange.response.redirectLocation && (
                                  <div>
                                    <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} font-semibold mb-1`}>Redirect Location</div>
                                    <code className={`block px-2 py-1 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} text-[10px] break-all`}>
                                      {exchange.response.redirectLocation}
                                    </code>
                                  </div>
                                )}
                                {exchange.artifacts && Object.keys(exchange.artifacts).length > 0 && (
                                  <div className={`p-2 rounded ${isDarkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'}`}>
                                    <div className="font-semibold mb-2 flex items-center gap-1">
                                      <FontAwesomeIcon icon={faKey} className="text-blue-500" />
                                      Extracted Artifacts
                                    </div>
                                    {exchange.artifacts.csrfToken && (
                                      <div className="text-[10px] mb-1">
                                        <span className="text-gray-400">CSRF:</span>
                                        <code className="ml-1 text-blue-400">{exchange.artifacts.csrfFieldName}: {exchange.artifacts.csrfToken.substring(0, 30)}...</code>
                                      </div>
                                    )}
                                    {exchange.artifacts.submittedFields?.length > 0 && (
                                      <div className="text-[10px]">
                                        <span className="text-gray-400">Fields:</span>
                                        <span className="ml-1">{exchange.artifacts.submittedFields.length} submitted</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            {getExchangeTab(index, 'response') === 'raw' && (
                              <pre className={`p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} overflow-x-auto text-[10px] font-mono whitespace-pre-wrap`}>
                                {`HTTP ${exchange.response.statusCode}\n\n`}
                                {Object.entries(formatHeaders(exchange.response.headers)).map(([k, v]) => `${k}: ${v}`).join('\n')}
                                {exchange.response.body && `\n\n${exchange.response.body.substring(0, 1000)}${exchange.response.body.length > 1000 ? '...' : ''}`}
                              </pre>
                            )}
                            {getExchangeTab(index, 'response') === 'headers' && (
                              <div className="space-y-1">
                                {Object.entries(formatHeaders(exchange.response.headers)).map(([name, value]) => (
                                  <div key={name} className={`p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <div className="text-[10px] font-semibold text-green-400">{name}</div>
                                    <div className="text-[10px] font-mono text-gray-300 break-all">{value}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-4 border-t ${borderClass}`}>
          <div className="text-sm text-gray-500">
            {workflow.exchanges?.length || 0} exchanges • {workflow.metadata?.totalDuration || 0}ms total
          </div>
          <div className="flex gap-2">
            {onTestReplay && (
              <button
                onClick={() => onTestReplay(workflow)}
                className={`px-4 py-2 rounded ${isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                Test Replay
              </button>
            )}
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'} text-white`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginWorkflowViewer;
